// De Collega-uitnodiging voor een groepsactiviteit (ClickUp 86c47j3zm, Figma
// sectie 6989:45250). De server zegt bij het openen van de link wat er aan de
// hand is (GET /v1/invites/{token}); dit vertaalt die toestand naar de popup die
// erbij hoort en naar de knoppen in de volgorde van het ontwerp.
//
// Zonder imports: scripts/test-invite-outcome.mjs laadt dit bestand in een vm.

// Zonder sessie: eerst inloggen of een account aanmaken.
const NEEDS_SESSION = ["login", "register"];

// De uitkomsten die een eigen popup van 640 breed krijgen (Figma 6989:27270 e.a.).
const OUTCOMES = [
  "full_alternative_dates",
  "full_alternative_activities",
  "full_no_alternatives",
  "waitlisted",
  "waitlist_refused",
  "insufficient_credits",
  "already_enrolled",
  "invalid",
];

/**
 * Welke dialog bij een toestand hoort: `respond` is "Uitnodiging goedkeuren"
 * (Figma 6971:76740), `outcome` een van de uitkomstpopups, `session` betekent
 * eerst inloggen of registreren. Onbekend telt als ongeldig: een link waar de
 * server niets mee kan, mag niet stil in het niets eindigen.
 */
export function inviteDialogFor(state) {
  if (state === "pending") return "respond";
  if (NEEDS_SESSION.includes(state)) return "session";

  return "outcome";
}

/**
 * De knoppen van een uitkomstpopup, in de volgorde van Figma. `primary` is de
 * gevulde knop. "Zet mij op de wachtlijst" verschijnt alleen als de wachtlijst
 * aan staat voor deze activiteit; staat hij uit, dan houdt "geen alternatieven"
 * alleen Sluiten en Naar dashboard over.
 *
 * Acties: `close` sluit de popup (je blijft waar je bent), `dashboard` gaat naar
 * het dashboard, `other_dates` sluit op de activiteitpagina waar de andere
 * tijdsloten staan, `other_activities` gaat naar het dashboard met de
 * activiteiten waarvoor je je nog kunt aanmelden, `waitlist` zet je op de
 * wachtlijst.
 */
export function inviteOutcomeButtons(state, waitlistAvailable) {
  const waitlist = waitlistAvailable ? [{ action: "waitlist", primary: false }] : [];
  const closeAndDashboard = [
    { action: "close", primary: false },
    { action: "dashboard", primary: true },
  ];

  switch (state) {
    case "full_alternative_dates":
      return [{ action: "other_dates", primary: true }, ...waitlist];
    case "full_alternative_activities":
      return [...waitlist, { action: "other_activities", primary: true }];
    case "full_no_alternatives":
      return waitlistAvailable
        ? [
            { action: "close", primary: false },
            { action: "waitlist", primary: true },
          ]
        : closeAndDashboard;
    default:
      return closeAndDashboard;
  }
}

/** De i18n-sleutel van titel en tekst; een onbekende toestand leest als ongeldig. */
export function inviteOutcomeKey(state) {
  return OUTCOMES.includes(state) ? state : "invalid";
}

/**
 * De ontvangers uit de collegakiezer als de body die de API leest: van een
 * collega alleen het id (ADR-0071), van een adres zonder account de tekst.
 */
export function toInvitePayload(recipients) {
  const list = Array.isArray(recipients) ? recipients : [];

  return {
    colleagues: list.filter((r) => r && r.id).map((r) => r.id),
    emails: list.filter((r) => r && r.email).map((r) => r.email),
  };
}

/** De adressen die de server weigerde, uit een 400 met `errors` (`emails.2` → het derde adres). */
export function refusedEmails(errors, emails) {
  if (!errors || typeof errors !== "object" || !Array.isArray(emails)) return [];

  return Object.keys(errors)
    .map((key) => /^emails\.(\d+)$/.exec(key))
    .filter(Boolean)
    .map((match) => emails[Number(match[1])])
    .filter(Boolean);
}

/**
 * Of je vanaf een eigen aanmelding een collega kunt uitnodigen: een
 * groepsactiviteit, een plek (geen wachtlijstplek), een tijdslot dat nog niet
 * begonnen en niet geannuleerd is en een bedrijfspagina met de collegakiezer aan.
 * De server toetst het opnieuw; dit verbergt alleen de knop.
 *
 * Het dashboard krijgt de aanmeldingen vanaf middernacht vandaag, dus ook een
 * tijdslot van vanochtend; de server weigert dat met 409 "Dit tijdslot is al
 * begonnen." (GroupInviteService, InviteSlotDetails::startsAt). `date` is de
 * epoch van Amsterdam-middernacht, `start` "HH:MM" -- dezelfde som als de
 * Afmeldtermijn (util/cancellationWindow). Een rij zonder datum laat de server
 * beslissen.
 */
export function canInviteFor(enroll, page, now = Date.now()) {
  return (
    !!enroll &&
    enroll.type === "groepsverband" &&
    enroll.status !== "waitlisted" &&
    !enroll.slot_canceled &&
    !!(page && page.settings && page.settings.invite_colleagues) &&
    !hasStarted(enroll, now)
  );
}

function hasStarted(enroll, now) {
  if (enroll.date === undefined || enroll.date === null || enroll.date === "") return false;
  const [hours = 0, minutes = 0] = String(enroll.start ?? "").split(":").map(Number);
  const startsAt = (Number(enroll.date) + hours * 3600 + minutes * 60) * 1000;

  return Number.isFinite(startsAt) && startsAt <= now;
}

/**
 * Waar de link uit de mail (`/uitnodiging/:token`) heen stuurt, gegeven wat de
 * server antwoordde en of er een sessie is. Null: blijf hier en toon de popup
 * "niet meer geldig" (alleen zonder sessie; met een sessie opent die op het
 * dashboard).
 */
export function inviteLandingTarget(data, loggedIn, token) {
  const back = `/uitnodiging/${token}`;
  const state = data && data.state;
  const param = encodeURIComponent(token);

  // `register`: een account van buiten de pagina dat de Toelatingsregel toelaat,
  // komt anders niet binnen (ADR-0145). Voor een lid doet het niets.
  if (state === "login") {
    return `/inloggen?register=true&r=${encodeURIComponent(back)}`;
  }
  if (state === "register") {
    return `/registreren?email=${encodeURIComponent((data && data.email) || "")}&r=${encodeURIComponent(back)}`;
  }
  if (data && data.invite && data.invite.activity && data.invite.activity.id) {
    return `/activiteiten/${data.invite.activity.id}?uitnodiging=${param}`;
  }

  return loggedIn ? `/?uitnodiging=${param}` : null;
}
